import type { GameInputAction } from './InputAction';
import { t } from '../../i18n';

export type InputDevice = 'keyboard-mouse' | 'gamepad';

export interface GamepadAxisBinding {
  readonly axis: number;
  readonly direction: -1 | 1;
}

export interface InputActionBinding {
  readonly keyboardCodes: readonly string[];
  readonly mouseButtons: readonly number[];
  readonly gamepadButtons: readonly number[];
  readonly gamepadAxes: readonly GamepadAxisBinding[];
}

const binding = (
  keyboardCodes: readonly string[] = [],
  mouseButtons: readonly number[] = [],
  gamepadButtons: readonly number[] = [],
  gamepadAxes: readonly GamepadAxisBinding[] = [],
): InputActionBinding => freezeInputBinding({ keyboardCodes, mouseButtons, gamepadButtons, gamepadAxes });

export function freezeInputBinding(value: InputActionBinding): InputActionBinding {
  return Object.freeze({
    keyboardCodes: Object.freeze([...value.keyboardCodes]),
    mouseButtons: Object.freeze([...value.mouseButtons]),
    gamepadButtons: Object.freeze([...value.gamepadButtons]),
    gamepadAxes: Object.freeze(value.gamepadAxes.map((axis) => Object.freeze({ ...axis }))),
  });
}

/** Single source of truth for the default physical bindings of semantic gameplay actions. */
export const DEFAULT_INPUT_ACTION_BINDINGS: Readonly<Record<GameInputAction, InputActionBinding>> = Object.freeze({
  'throttle-forward': binding(['KeyW'], [], [7]),
  'throttle-reverse': binding(['KeyS'], [], [6]),
  'steer-left': binding(['KeyA'], [], [], [{ axis: 0, direction: -1 }]),
  'steer-right': binding(['KeyD'], [], [], [{ axis: 0, direction: 1 }]),
  'pitch-up': binding(['KeyS'], [], [], [{ axis: 1, direction: 1 }]),
  'pitch-down': binding(['KeyW'], [], [], [{ axis: 1, direction: -1 }]),
  jump: binding([], [2], [0]),
  boost: binding([], [0], [1]),
  handbrake: binding(['ShiftLeft', 'ShiftRight'], [], [2]),
  'air-roll-left': binding(['KeyQ']),
  'air-roll-right': binding(['KeyE']),
  'toggle-ball-camera': binding(['Space'], [], [3]),
  'camera-look-left': binding(['ControlLeft'], [], [], [{ axis: 2, direction: -1 }]),
  'camera-look-right': binding(['AltLeft'], [], [], [{ axis: 2, direction: 1 }]),
  'camera-look-up': binding([], [], [], [{ axis: 3, direction: -1 }]),
  'camera-look-down': binding([], [], [], [{ axis: 3, direction: 1 }]),
  'rear-view': binding([], [1], [5]),
  'show-match-roster': binding(['Tab'], [], [8]),
  'open-chat': binding(['KeyT']),
  'skip-replay': binding([], [2], [0]),
  'open-session-menu': binding(['Escape'], [], [9]),
  'spectate-next': binding(['ArrowRight'], [], [15]),
  'spectate-previous': binding(['ArrowLeft'], [], [14]),
  'training-take-possession': binding(['Digit1'], [], [13]),
  'training-start-dribble': binding(['Digit2'], [], [12]),
  'training-pass-ball': binding(['Digit3'], [], [14]),
  'training-reset': binding(['Backspace'], [], [11]),
});

const GAMEPAD_BUTTON_LABELS: Readonly<Record<number, string>> = Object.freeze({
  0: 'A / ×', 1: 'B / ○', 2: 'X / □', 3: 'Y / △',
  4: 'LB / L1', 5: 'RB / R1', 6: 'LT / L2', 7: 'RT / R2',
  8: 'VIEW / SHARE', 9: 'MENU / OPTIONS', 10: 'L3', 11: 'R3',
  12: 'D-PAD ↑', 13: 'D-PAD ↓', 14: 'D-PAD ←', 15: 'D-PAD →',
});

export function inputBindingLabel(binding: InputActionBinding, device: InputDevice): string {
  if (device === 'gamepad') {
    const button = binding.gamepadButtons[0];
    if (button !== undefined) return gamepadButtonLabel(button);
    const axis = binding.gamepadAxes[0];
    if (axis !== undefined) return gamepadAxisLabel(axis);
    return t('settings:controls.bindings.unbound');
  }

  const mouseButton = binding.mouseButtons[0];
  if (mouseButton !== undefined) return mouseButtonLabel(mouseButton);
  const code = binding.keyboardCodes[0];
  if (code !== undefined) return keyboardCodeLabel(code);
  return t('settings:controls.bindings.unbound');
}

export function gamepadButtonLabel(button: number): string {
  return GAMEPAD_BUTTON_LABELS[button] ?? t('settings:controls.bindings.gamepadButton', { button });
}

export function gamepadAxisLabel(binding: GamepadAxisBinding): string {
  const direction = binding.direction < 0 ? -1 : 1;
  if (binding.axis === 0) return direction < 0
    ? t('settings:controls.bindings.leftStickLeft')
    : t('settings:controls.bindings.leftStickRight');
  if (binding.axis === 1) return direction < 0
    ? t('settings:controls.bindings.leftStickUp')
    : t('settings:controls.bindings.leftStickDown');
  if (binding.axis === 2) return direction < 0
    ? t('settings:controls.bindings.rightStickLeft')
    : t('settings:controls.bindings.rightStickRight');
  if (binding.axis === 3) return direction < 0
    ? t('settings:controls.bindings.rightStickUp')
    : t('settings:controls.bindings.rightStickDown');
  return t('settings:controls.bindings.axis', { axis: binding.axis + 1, direction: direction < 0 ? '−' : '+' });
}

export function mouseButtonLabel(button: number): string {
  if (button === 0) return t('settings:controls.bindings.mouseLeft');
  if (button === 1) return t('settings:controls.bindings.mouseMiddle');
  if (button === 2) return t('settings:controls.bindings.mouseRight');
  if (button === 3) return t('settings:controls.bindings.mouseBack');
  if (button === 4) return t('settings:controls.bindings.mouseForward');
  return t('settings:controls.bindings.mouseButton', { button: button + 1 });
}

export function keyboardCodeLabel(code: string): string {
  if (code === 'Space') return 'SPACEBAR';
  if (code === 'Escape') return 'ESC';
  if (code === 'Backspace') return 'BACKSPACE';
  if (code === 'Tab') return 'TAB';
  if (code === 'ControlLeft') return 'CTRL';
  if (code === 'ControlRight') return t('settings:controls.bindings.rightCtrl');
  if (code === 'AltLeft') return 'ALT';
  if (code === 'AltRight') return t('settings:controls.bindings.rightAlt');
  if (code === 'ShiftLeft') return 'SHIFT';
  if (code === 'ShiftRight') return t('settings:controls.bindings.rightShift');
  if (code.startsWith('Digit')) return code.slice('Digit'.length);
  if (code.startsWith('Key')) return code.slice('Key'.length);
  if (code === 'ArrowLeft') return '←';
  if (code === 'ArrowRight') return '→';
  if (code === 'ArrowUp') return '↑';
  if (code === 'ArrowDown') return '↓';
  return code.toUpperCase();
}
